'use client';

import { useCallback, useEffect, useRef } from 'react';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { templates } from '@packages/modules/src/helpers/getResponsiveAkamaiTemplate/config';
import { useActiveSlide } from '../ImageGalleryProvider/ImageGalleryProvider.client';

type GalleryImage = {
  src: string;
  alt: string;
};

export const ImageGalleryThumbnails = ({ images }: { images: GalleryImage[] }) => {
  const [, setActiveSlide] = useActiveSlide();
  const thumbnailsContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<HTMLButtonElement[]>([]);
  const galleryRef = useRef<HTMLElement>(null);

  const centerActiveThumbnail = (index: number) => {
    const container = thumbnailsContainerRef.current;
    const thumbnail = thumbnailRefs.current[index];

    if (!container || !thumbnail) return;

    // calculate the center position
    const containerWidth = container.offsetWidth;
    const thumbnailWidth = thumbnail.offsetWidth;
    const thumbnailLeft = thumbnail.offsetLeft;

    // calculate the scroll position that centers the thumbnail
    const scrollPosition = thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;

    // scroll the container to calculated position
    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
  };

  const setSelectedThumbnail = useCallback(
    (index: number) => {
      centerActiveThumbnail(index);
      setActiveSlide(index);

      thumbnailRefs.current.forEach((thumb) => thumb?.classList.remove('ring-2'));
      thumbnailRefs.current[index].classList.add('ring-2');
    },
    [setActiveSlide],
  );

  // sets up event listeners only once on mount
  useEffect(() => {
    galleryRef.current = document.getElementById('gallery-scroll');
    if (!galleryRef.current) return;
    // debounce function to only run when scrolling stops (when no scrolling happend since `delay` ms)
    const debounce = (fn: () => void, delay: number) => {
      let timeoutId: NodeJS.Timeout;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(), delay);
      };
    };
    // Update active thumbnail on scroll end
    const handleScrollEnd = () => {
      if (!galleryRef.current) return;
      const scrollPosition = galleryRef.current.scrollLeft;
      const slideWidth = galleryRef.current.clientWidth;
      const newIndex = Math.round(scrollPosition / slideWidth);
      setSelectedThumbnail(newIndex);
    };
    const debouncedHandleScroll = debounce(handleScrollEnd, 100);

    // when Safari supports `scrollend` event, we can use it instead of debouncing
    galleryRef.current.addEventListener('scroll', handleScrollEnd);
    // eslint-disable-next-line consistent-return
    return () => {
      if (galleryRef.current) {
        galleryRef.current.removeEventListener('scroll', handleScrollEnd);
      }
    };
  }, [setSelectedThumbnail]);

  const handleThumbnailClick = (index: number) => {
    if (!galleryRef.current) return;

    const slideWidth = galleryRef.current.clientWidth;
    galleryRef.current.scrollTo({
      left: slideWidth * index,
      behavior: 'smooth',
    });
    setSelectedThumbnail(index);
  };

  return (
    <div
      ref={thumbnailsContainerRef}
      className="scrollbar-hide flex gap-2 overflow-x-auto px-0.5 py-0.5"
    >
      {images.map(({ src, alt }, index) => (
        <button
          type="button"
          ref={(el) => {
            if (el) {
              thumbnailRefs.current[index] = el;
            }
          }}
          key={src}
          onClick={() => handleThumbnailClick(index)}
          id={`thumbnail-${index}`}
          className={`ring-color-text-dark thumbnails flex-shrink-0 cursor-pointer rounded transition-all duration-500 ${index === 0 ? 'ring-2' : ''}`}
          aria-label={`View ${alt}`}
        >
          <div className="relative h-[60px] w-[60px] overflow-hidden rounded md:h-[96px] md:w-[96px]">
            <Image
              src={`${src}?${templates.thumbnail.mobile.templateName}`}
              alt={alt}
              fill
              className="object-contain"
              unoptimized
            />
          </div>
        </button>
      ))}
    </div>
  );
};
